import React, { useEffect } from "react";

export type ToastType = "success" | "error" | "warning" | "info";

interface ToastProps {
  show: boolean;
  type: ToastType;
  title: string;
  message?: string;
  onClose: () => void;
  duration?: number; // milliseconds, default 3000
}

const Toast: React.FC<ToastProps> = ({ show, type, title, message, onClose, duration = 3000 }) => {
  useEffect(() => {
    if (show && duration > 0) {
      const timer = setTimeout(() => {
        onClose();
      }, duration);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [show, duration, onClose]);

  if (!show) return null;

  const typeConfig = {
    success: {
      bgColor: "bg-green-50 dark:bg-green-900",
      borderColor: "border-green-200 dark:border-green-700",
      textColor: "text-green-800 dark:text-green-100",
      iconColor: "text-green-600 dark:text-green-300",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    error: {
      bgColor: "bg-red-50 dark:bg-red-900",
      borderColor: "border-red-200 dark:border-red-700",
      textColor: "text-red-800 dark:text-red-100",
      iconColor: "text-red-600 dark:text-red-300",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
    warning: {
      bgColor: "bg-yellow-50 dark:bg-yellow-900",
      borderColor: "border-yellow-200 dark:border-yellow-700",
      textColor: "text-yellow-800 dark:text-yellow-100",
      iconColor: "text-yellow-600 dark:text-yellow-300",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
          />
        </svg>
      ),
    },
    info: {
      bgColor: "bg-blue-50 dark:bg-blue-900",
      borderColor: "border-blue-200 dark:border-blue-700",
      textColor: "text-blue-800 dark:text-blue-100",
      iconColor: "text-blue-600 dark:text-blue-300",
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
      ),
    },
  };

  const config = typeConfig[type];

  return (
    <div className="fixed top-4 right-4 z-[200] max-w-sm transition-all duration-300 ease-in-out transform">
      <div
        className={`flex items-center justify-between p-4 ${config.bgColor} border ${config.borderColor} rounded-lg shadow-lg`}
      >
        <div className="flex items-center flex-1">
          <div className={`${config.iconColor} mr-3 flex-shrink-0`}>{config.icon}</div>
          <div className="flex-1 min-w-0">
            <div className={`font-medium ${config.textColor}`}>{title}</div>
            {message != null && message !== "" && (
              <div className={`text-sm ${config.textColor} opacity-90 mt-1`}>{message}</div>
            )}
          </div>
        </div>
        <button
          className={`${config.iconColor} hover:opacity-75 ml-4 flex-shrink-0`}
          onClick={onClose}
          aria-label="Close notification"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default Toast;
